import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Send, Lock, UserPlus, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { EmptyState } from "../../components/shared/EmptyState";
import { api, formatApiError } from "../../lib/api";

// v1.6 — pipeline commercial des demandes (formulaires de contact)
const LEAD_STATUSES = [
  ["nouvelle", "Nouvelle demande"],
  ["a_qualifier", "À qualifier"],
  ["a_contacter", "À contacter"],
  ["devis_envoye", "Devis envoyé"],
  ["attente_client", "En attente du client"],
  ["confirmee", "Confirmée"],
  ["cloturee", "Clôturée"],
];
const LEAD_LABELS = Object.fromEntries(LEAD_STATUSES);

export default function MessagesPage() {
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("threads"); // threads | contact
  const [leads, setLeads] = useState(null);
  const [lead, setLead] = useState(null); // fiche individuelle de la demande
  const [leadStatusFilter, setLeadStatusFilter] = useState("");
  const [leadSearch, setLeadSearch] = useState("");
  const [note, setNote] = useState("");
  const [leadReply, setLeadReply] = useState("");

  const loadThreads = () => api.get("/admin/messages/threads").then((r) => setThreads(r.data)).catch(() => setThreads([]));
  const loadLeads = () => {
    const params = {};
    if (leadStatusFilter) params.status = leadStatusFilter;
    if (leadSearch) params.q = leadSearch;
    api.get("/admin/contact-messages", { params }).then((r) => setLeads(r.data)).catch(() => setLeads([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadThreads(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLeads(); }, [leadStatusFilter, leadSearch]);

  const openLead = async (l) => {
    try {
      const { data } = await api.get(`/admin/contact-messages/${l.id}`);
      setLead(data);
      setNote("");
      setLeadReply("");
      if (data.status === "nouvelle") {
        await api.patch(`/admin/contact-messages/${l.id}`, { status: "a_qualifier" }).catch(() => {});
        loadLeads();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const leadAct = async (fn, success) => {
    try { await fn(); toast.success(success); openLead(lead); loadLeads(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openThread = async (t) => {
    try {
      const { data } = await api.get(`/admin/messages/threads/${t.id}`);
      setActive(data);
      loadThreads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const send = async () => {
    if (!reply.trim() || !active) return;
    try {
      await api.post(`/admin/messages/threads/${active.id}/reply`, { body: reply, internal });
      setReply("");
      setInternal(false);
      openThread(active);
      toast.success(internal ? "Note interne ajoutée (invisible pour le client)" : "Réponse rattachée au fil — e-mail externe simulé");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!threads) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="messages-loading">Chargement…</div>;

  const filtered = threads.filter((t) => `${t.subject} ${t.customer_email} ${t.customer_name || ""}`.toLowerCase().includes(search.toLowerCase()));
  const newLeads = (leads || []).filter((m) => m.status === "nouvelle").length;

  return (
    <div data-testid="admin-messages-page">
      <p className="overline-ae">B12 — Messages &amp; Communications</p>
      <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Messages &amp; communications</h1>

      <div className="mt-6 flex gap-2" role="tablist">
        <button type="button" onClick={() => setTab("threads")} data-testid="messages-tab-threads"
          className={tab === "threads" ? "btn-primary-ae !px-5 !py-2 !text-xs" : "btn-secondary-ae !px-5 !py-2 !text-xs"}>
          Conversations clients
        </button>
        <button type="button" onClick={() => setTab("contact")} data-testid="messages-tab-contact"
          className={tab === "contact" ? "btn-primary-ae !px-5 !py-2 !text-xs" : "btn-secondary-ae !px-5 !py-2 !text-xs"}>
          Demandes (formulaires)
          {newLeads > 0 && (
            <span className="ml-1 rounded-full bg-gold px-2 py-0.5 text-[10px] font-bold text-deepgreen" data-testid="contact-unread-badge">{newLeads}</span>
          )}
        </button>
      </div>

      {tab === "contact" && (
        <div className="card-ae mt-4 grid lg:grid-cols-3" data-testid="admin-contact-messages">
          <div className="border-b border-border lg:border-b-0 lg:border-r">
            <div className="space-y-2 border-b border-border p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input className="input-ae !pl-9" placeholder="Rechercher (nom, e-mail, type…)…" value={leadSearch} onChange={(e) => setLeadSearch(e.target.value)} data-testid="lead-search" />
              </div>
              <select className="input-ae" value={leadStatusFilter} onChange={(e) => setLeadStatusFilter(e.target.value)} data-testid="lead-status-filter" aria-label="Filtrer par statut commercial">
                <option value="">Tous les statuts</option>
                {LEAD_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {!leads ? (
              <p className="p-4 font-heading text-deepgreen">Chargement…</p>
            ) : leads.length === 0 ? (
              <div className="p-4"><EmptyState title="Aucune demande">Les demandes envoyées via les formulaires du site apparaîtront ici.</EmptyState></div>
            ) : (
              <ul className="max-h-[60vh] divide-y divide-border overflow-y-auto" data-testid="admin-contact-messages-list">
                {leads.map((m) => (
                  <li key={m.id}>
                    <button type="button" onClick={() => openLead(m)} data-testid={`contact-message-${m.id}`}
                      className={`w-full px-4 py-3.5 text-left transition-colors ${lead?.id === m.id ? "bg-sage-light" : "hover:bg-cream/60"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-deepgreen">{m.first_name} {m.name}</p>
                        <span className="rounded-full bg-cream px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-deepgreen" data-testid={`lead-status-${m.id}`}>{LEAD_LABELS[m.status] || m.status}</span>
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {m.subject} — {(m.created_at || "").slice(0, 16).replace("T", " ")}
                      </p>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {m.incomplete && <span className="rounded-full bg-gold-light px-2 py-0.5 text-[10px] font-bold text-deepgreen">Date à définir</span>}
                        {m.duplicate && <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-700"><AlertTriangle className="h-3 w-3" /> Doublon possible</span>}
                        {m.customer_id && <span className="rounded-full bg-sage-light px-2 py-0.5 text-[10px] font-bold text-deepgreen">Fiche client liée</span>}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="p-5 lg:col-span-2" data-testid="lead-detail">
            {!lead ? (
              <EmptyState title="Sélectionnez une demande">Sa fiche complète s'affiche ici : coordonnées, événement, notes, historique et actions commerciales.</EmptyState>
            ) : (
              <div className="space-y-5">
                <div>
                  <h2 className="font-heading text-lg text-deepgreen" data-testid="lead-name">{lead.first_name} {lead.name}</h2>
                  <p className="text-xs text-muted-foreground">
                    {lead.email} — {lead.phone} — reçue le {(lead.created_at || "").slice(0, 16).replace("T", " ")} via {lead.source === "accueil" ? "l'accueil" : "la page Contact"}
                  </p>
                  {lead.customer_id && (
                    <Link to={`/admin/clients/${lead.customer_id}`} className="mt-1 inline-flex items-center gap-1.5 text-xs font-bold text-deepgreen hover:text-sage" data-testid="lead-customer-link">
                      Ouvrir la fiche client →
                    </Link>
                  )}
                </div>
                <div className="grid gap-3 rounded-lg bg-cream p-4 text-sm sm:grid-cols-2" data-testid="lead-event">
                  <p><span className="text-muted-foreground">Type :</span> <strong>{lead.subject}</strong></p>
                  <p><span className="text-muted-foreground">Date :</span> <strong>{lead.event_date || (lead.event_date_tbd ? "À définir (demande incomplète)" : "—")}</strong></p>
                  {lead.venue && <p><span className="text-muted-foreground">Lieu :</span> {lead.venue}</p>}
                  {lead.guests && <p><span className="text-muted-foreground">Invités :</span> {lead.guests}</p>}
                  {lead.budget && <p><span className="text-muted-foreground">Budget :</span> {lead.budget}</p>}
                </div>
                <div>
                  <p className="label-ae">Message initial</p>
                  <p className="whitespace-pre-line rounded-lg bg-white p-3 text-sm text-foreground/85" data-testid="lead-message">{lead.message}</p>
                </div>
                {(lead.duplicates || []).length > 0 && (
                  <div className="rounded-lg border border-gold/50 bg-gold-light p-3 text-xs" data-testid="lead-duplicates">
                    <p className="font-bold text-deepgreen">Doublons possibles (même e-mail/téléphone) :</p>
                    {lead.duplicates.map((d) => <p key={d.id} className="mt-1 text-muted-foreground">• {d.subject} — {(d.created_at || "").slice(0, 10)} — {LEAD_LABELS[d.status] || d.status}</p>)}
                  </div>
                )}
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="label-ae" htmlFor="lead-status">Statut commercial</label>
                    <select id="lead-status" className="input-ae" value={lead.status} onChange={(e) => leadAct(() => api.patch(`/admin/contact-messages/${lead.id}`, { status: e.target.value }), "Statut mis à jour")} data-testid="lead-status-select">
                      {LEAD_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div className="flex items-end gap-2">
                    {!lead.customer_id && (
                      <button type="button" onClick={() => leadAct(() => api.post(`/admin/contact-messages/${lead.id}/create-customer`), "Fiche client créée / rattachée")} className="btn-secondary-ae !px-4 !py-2 !text-[11px]" data-testid="lead-create-customer">
                        <UserPlus className="h-3.5 w-3.5" /> Créer la fiche client
                      </button>
                    )}
                    <Link to="/admin/devis" className="btn-secondary-ae !px-4 !py-2 !text-[11px]" data-testid="lead-create-quote">
                      <FileText className="h-3.5 w-3.5" /> Créer un devis
                    </Link>
                  </div>
                </div>
                <div>
                  <p className="label-ae">Répondre au prospect</p>
                  <div className="flex gap-2">
                    <input className="input-ae" placeholder="Votre réponse (tracée dans l'historique)…" value={leadReply} onChange={(e) => setLeadReply(e.target.value)} data-testid="lead-reply" />
                    <button type="button" disabled={leadReply.trim().length < 2} onClick={() => leadAct(() => api.post(`/admin/contact-messages/${lead.id}/reply`, { body: leadReply }).then(() => setLeadReply("")), "Réponse enregistrée — e-mail externe simulé")} className="btn-primary-ae !px-4 disabled:opacity-50" data-testid="lead-reply-send" aria-label="Répondre"><Send className="h-4 w-4" /></button>
                  </div>
                </div>
                <div>
                  <p className="label-ae">Notes internes</p>
                  <div className="flex gap-2">
                    <input className="input-ae" placeholder="Note équipe Art Event…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="lead-note" />
                    <button type="button" disabled={note.trim().length < 2} onClick={() => leadAct(() => api.post(`/admin/contact-messages/${lead.id}/notes`, { note }).then(() => setNote("")), "Note ajoutée")} className="btn-secondary-ae !px-4 disabled:opacity-50" data-testid="lead-note-add">Ajouter</button>
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {(lead.notes || []).map((n, i) => (
                      <li key={i} className="rounded-lg border border-dashed border-gold bg-gold-light/50 p-2.5 text-xs text-deepgreen" data-testid={`lead-note-${i}`}>
                        <Lock className="mr-1 inline h-3 w-3" />{n.note} <span className="text-muted-foreground">— {n.by}, {(n.at || "").slice(0, 16).replace("T", " ")}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="label-ae">Historique des échanges</p>
                  <ul className="space-y-1.5" data-testid="lead-history">
                    {(lead.history || []).map((h, i) => (
                      <li key={i} className="rounded-lg bg-cream px-3 py-2 text-xs" data-testid={`lead-history-${i}`}>
                        <span className="font-semibold text-deepgreen">{h.label}</span>
                        <span className="text-muted-foreground">{h.by ? ` — ${h.by}` : ""} — {(h.at || "").slice(0, 16).replace("T", " ")}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "threads" && (
      <div className="card-ae mt-4 grid lg:grid-cols-3">
        <div className="border-b border-border lg:border-b-0 lg:border-r">
          <div className="border-b border-border p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input className="input-ae !pl-9" placeholder="Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-messages-search" />
            </div>
          </div>
          {filtered.length === 0 ? (
            <div className="p-4"><EmptyState title="Aucune conversation">Les messages des clients apparaîtront ici, rattachés au bon client et événement.</EmptyState></div>
          ) : (
            <ul className="divide-y divide-border" data-testid="admin-messages-list">
              {filtered.map((t) => (
                <li key={t.id}>
                  <button type="button" onClick={() => openThread(t)} data-testid={`admin-thread-${t.id}`}
                    className={`w-full px-4 py-3.5 text-left transition-colors ${active?.id === t.id ? "bg-sage-light" : "hover:bg-cream/60"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-deepgreen">{t.customer_name || t.customer_email}</p>
                      {t.unread_admin > 0 && <span className="rounded-full bg-gold px-2 py-0.5 text-[10px] font-bold text-deepgreen" data-testid={`thread-unread-${t.id}`}>{t.unread_admin}</span>}
                    </div>
                    <p className="mt-0.5 text-xs font-semibold text-foreground/70">{t.subject}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{(t.last_message_at || "").slice(0, 16).replace("T", " ")}{t.event_id ? " — rattaché à un événement" : ""}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="p-5 lg:col-span-2" data-testid="admin-message-thread">
          {!active ? (
            <EmptyState title="Sélectionnez une conversation">Le fil complet s'affiche ici, avec les notes internes séparées.</EmptyState>
          ) : (
            <>
              <div>
                <h2 className="font-heading text-lg text-deepgreen" data-testid="thread-subject">{active.subject}</h2>
                <p className="text-xs text-muted-foreground">{active.customer_email}{active.event_id ? " — événement rattaché" : ""}{active.reservation_id ? ` — réservation rattachée` : ""}</p>
              </div>
              <div className="mt-5 max-h-96 space-y-3 overflow-y-auto" data-testid="thread-messages">
                {active.messages.map((m) => (
                  <div key={m.id} className={m.internal
                    ? "mx-auto max-w-md rounded-xl border-2 border-dashed border-gold bg-gold-light/60 p-4 text-sm text-deepgreen"
                    : m.author === "client"
                      ? "max-w-md rounded-xl rounded-tl-none bg-cream p-4 text-sm text-foreground/85"
                      : "ml-auto max-w-md rounded-xl rounded-tr-none bg-deepgreen p-4 text-sm text-cream"
                  } data-testid={`message-${m.id}`}>
                    {m.internal && <p className="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-gold"><Lock className="h-3 w-3" /> Note interne — invisible pour le client</p>}
                    {m.body}
                    <p className={`mt-2 text-[10px] ${m.internal ? "text-deepgreen/60" : m.author === "client" ? "text-muted-foreground" : "text-cream/60"}`}>
                      {m.author === "client" ? active.customer_email : "Art Event"} — {(m.created_at || "").slice(0, 16).replace("T", " ")}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-2">
                <input className="input-ae" placeholder={internal ? "Note interne (équipe uniquement)…" : "Votre réponse au client…"} value={reply} onChange={(e) => setReply(e.target.value)} data-testid="admin-message-reply" />
                <button type="button" onClick={send} className="btn-primary-ae !px-5" data-testid="admin-message-send" aria-label="Envoyer"><Send className="h-4 w-4" /></button>
              </div>
              <label className="mt-3 flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} className="h-4 w-4 accent-[#2C4C3B]" data-testid="admin-message-internal" />
                Note interne — séparée des messages visibles par le client
              </label>
            </>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
